import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  People as PeopleIcon,
  Description as DescriptionIcon,
  LocationOn as LocationOnIcon,
  Assessment as AssessmentIcon,
  Logout as LogoutIcon,
  ExpandLess,
  ExpandMore,
  SmartToy as ChatbotIcon,
  LibraryBooks as KnowledgeBaseIcon,
} from '@mui/icons-material';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [formsOpen, setFormsOpen] = useState(true);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const getPortalLabel = () => {
    try {
      const user = JSON.parse(localStorage.getItem('admin_user') || 'null');
      if (user?.role === 'TALUKA_ADMIN') return 'Taluka Admin Portal';
      return 'Admin Portal';
    } catch {
      return 'Admin Portal';
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/analytics')) return { title: 'Analytics', subtitle: 'Overview of activity metrics' };
    if (path.includes('/users')) return { title: 'वापरकर्ते', subtitle: 'User Management' };
    if (path.includes('/forms/hbyc')) return { title: 'HBYC फॉर्म', subtitle: 'Home Based Young Child Care' };
    if (path.includes('/forms/hbnc')) return { title: 'HBNC फॉर्म', subtitle: 'Home Based Newborn Care' };
    if (path.includes('/forms/nrc')) return { title: 'NRC फॉर्म', subtitle: 'Nutrition Rehabilitation Center Referral' };
    if (path.includes('/forms/sncu')) return { title: 'SNCU फॉर्म', subtitle: 'Small & Sick Newborn Care Unit Follow-up' };
    if (path.includes('/forms')) return { title: 'फॉर्म', subtitle: 'Form Management' };
    if (path.includes('/districts')) return { title: 'जिल्हे व तालुके', subtitle: 'Districts & Talukas' };
    if (path.includes('/chatbot')) return { title: 'Chatbot', subtitle: 'ASHA AI Assistant' };
    if (path.includes('/knowledge-base')) return { title: 'Knowledge Base', subtitle: 'Manage RAG document embeddings' };
    return { title: getPortalLabel(), subtitle: '' };
  };

  const pageInfo = getPageTitle();

  const handleFormsClick = () => {
    setFormsOpen(!formsOpen);
  };

  const menuItems = [
    { text: 'विश्लेषण', icon: <AssessmentIcon />, path: '/admin/analytics' },
    { text: 'वापरकर्ते', icon: <PeopleIcon />, path: '/admin/users' },
    {
      text: 'फॉर्म',
      icon: <DescriptionIcon />,
      hasSubmenu: true,
      submenu: [
        { text: 'HBYC', path: '/admin/forms/hbyc' },
        { text: 'HBNC', path: '/admin/forms/hbnc' },
        { text: 'NRC', path: '/admin/forms/nrc' },
        { text: 'SNCU', path: '/admin/forms/sncu' },
      ]
    },
    { text: 'जिल्हे व तालुके', icon: <LocationOnIcon />, path: '/admin/districts' },
    { text: 'Chatbot', icon: <ChatbotIcon />, path: '/admin/chatbot' },
    { text: 'Knowledge Base', icon: <KnowledgeBaseIcon />, path: '/admin/knowledge-base' },
  ];

  const isActiveRoute = (path) => {
    if (path === '/admin/analytics') {
      return location.pathname === path || location.pathname === '/admin';
    }
    return location.pathname === path;
  };

  const drawer = (isCollapsed) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          ZP
        </Avatar>
        {!isCollapsed && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: 'grey.900',
                lineHeight: 1.2,
              }}
            >
              ZP ASHA
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'grey.500', fontSize: '0.7rem' }}
            >
              {getPortalLabel()}
            </Typography>
          </Box>
        )}
        {!isCollapsed && (
          <IconButton
            onClick={handleCollapse}
            size="small"
            sx={{
              display: { xs: 'none', sm: 'flex' },
              color: 'grey.400',
              '&:hover': { color: 'grey.600', bgcolor: 'grey.100' },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 1, overflow: 'auto' }}>
        <List sx={{ px: 1.5 }}>
          {menuItems.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={!item.hasSubmenu && isActiveRoute(item.path)}
                  onClick={() => item.hasSubmenu ? handleFormsClick() : navigate(item.path)}
                  sx={{
                    borderRadius: 1.5,
                    minHeight: 44,
                    px: isCollapsed ? 1.5 : 2,
                    position: 'relative',
                    '&.Mui-selected': {
                      bgcolor: 'primary.lighter',
                      '&:hover': {
                        bgcolor: 'primary.lighter',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: 3,
                        height: 20,
                        bgcolor: 'primary.main',
                        borderRadius: '0 4px 4px 0',
                      },
                    },
                    '&:hover': {
                      bgcolor: 'grey.100',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: isCollapsed ? 'auto' : 40,
                      color: isActiveRoute(item.path) ? 'primary.main' : 'grey.500',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: isActiveRoute(item.path) ? 600 : 500,
                          color: isActiveRoute(item.path) ? 'primary.main' : 'grey.700',
                        }}
                      />
                      {item.hasSubmenu && (
                        <Box sx={{ color: 'grey.400' }}>
                          {formsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </Box>
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {item.hasSubmenu && !isCollapsed && (
                <Collapse in={formsOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ ml: 2.5 }}>
                    {item.submenu.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                          selected={location.pathname === subItem.path}
                          onClick={() => navigate(subItem.path)}
                          sx={{
                            borderRadius: 1.5,
                            minHeight: 36,
                            pl: 4,
                            position: 'relative',
                            '&.Mui-selected': {
                              bgcolor: 'primary.lighter',
                              '&:hover': {
                                bgcolor: 'primary.lighter',
                              },
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 3,
                                height: 16,
                                bgcolor: 'primary.main',
                                borderRadius: '0 4px 4px 0',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'grey.100',
                            },
                          }}
                        >
                          <ListItemText
                            primary={subItem.text}
                            primaryTypographyProps={{
                              fontSize: '0.8125rem',
                              fontWeight: location.pathname === subItem.path ? 600 : 400,
                              color: location.pathname === subItem.path ? 'primary.main' : 'grey.600',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Logout Section */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'grey.200' }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 1.5,
            minHeight: 44,
            px: isCollapsed ? 1.5 : 2,
            '&:hover': {
              bgcolor: 'error.lighter',
              '& .MuiListItemIcon-root': {
                color: 'error.main',
              },
              '& .MuiListItemText-primary': {
                color: 'error.main',
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: isCollapsed ? 'auto' : 40,
              color: 'grey.500',
            }}
          >
            <LogoutIcon />
          </ListItemIcon>
          {!isCollapsed && (
            <ListItemText
              primary="बाहेर पडा"
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'grey.700',
              }}
            />
          )}
        </ListItemButton>
      </Box>
    </Box>
  );

  const currentDrawerWidth = collapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <Box sx={{ display: 'flex', bgcolor: 'grey.50', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: 'width 0.2s ease, margin 0.2s ease',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' }, color: 'grey.700' }}
          >
            <MenuIcon />
          </IconButton>
          {collapsed && (
            <IconButton
              onClick={handleCollapse}
              sx={{
                mr: 2,
                display: { xs: 'none', sm: 'flex' },
                color: 'grey.500',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: 'grey.900',
                fontWeight: 600,
                fontSize: '1.125rem',
              }}
            >
              {pageInfo.title}
            </Typography>
            {pageInfo.subtitle && (
              <Typography
                variant="body2"
                sx={{ color: 'grey.500', fontSize: '0.8125rem' }}
              >
                {pageInfo.subtitle}
              </Typography>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.2s ease',
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'grey.50',
              border: 'none',
            },
          }}
        >
          {drawer(false)}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              transition: 'width 0.2s ease',
              overflowX: 'hidden',
              bgcolor: 'grey.50',
              border: 'none',
              borderRight: '1px solid',
              borderColor: 'grey.200',
            },
          }}
          open
        >
          {drawer(collapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          transition: 'width 0.2s ease',
          bgcolor: 'grey.50',
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }} />
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default AdminLayout;
